import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useGroupChatOptIn, useSetGroupChatOptIn } from "../api/hooks";

// One shared toggle — identical design for both roles (SPEC §5a). Only the copy differs,
// chosen from the signed-in user's role. Långiver = investor, Låntaker = loaner.
const COPY = {
  investor: {
    title: "La andre långivere skrive til meg i gruppe-chat",
    help: "Når dette er på, blir du med i gruppe-chatten for lånefasene dine, og andre långivere i samme fase kan skrive til deg. Av som standard.",
  },
  loaner: {
    title: "La meg delta i gruppe-chat for mine lån",
    help: "Når dette er på, blir du med i gruppe-chatten for lånene dine, slik at långiverne i samme fase kan skrive med deg. Av som standard.",
  },
} as const;

/** Self-contained opt-in switch: reads role + current state from the store/API and writes
 *  back optimistically. Drop it straight into the settings page. */
export function GroupChatToggle() {
  const role = useAuthStore((s) => s.user?.role);
  const copy = role === "loaner" ? COPY.loaner : COPY.investor;

  const { data: optIn, isLoading } = useGroupChatOptIn();
  const setOptIn = useSetGroupChatOptIn();

  const checked = optIn ?? false;

  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">{copy.title}</p>
        <p className="mt-1 text-sm text-white/55">{copy.help}</p>
        {setOptIn.isError && (
          <p className="mt-2 text-sm text-red-300">Kunne ikke lagre. Prøv igjen.</p>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={copy.title}
          disabled={setOptIn.isPending}
          onClick={() => setOptIn.mutate(!checked)}
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2a2e] disabled:opacity-50",
            checked ? "bg-teal-400" : "bg-white/10"
          )}
        >
          <span
            className={cn(
              "inline-block size-5 transform rounded-full bg-white shadow transition-transform",
              checked ? "translate-x-5" : "translate-x-0.5"
            )}
          />
        </button>
      )}
    </div>
  );
}
